import {AppUrls} from 'shared/config/model/urls';

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';

import {cityAPI} from 'features/admin/api/cityAPI';
import {zoneAPI} from 'features/admin/api/zoneAPI';
import {countryAPI} from 'features/admin/api/countryAPI';
import {locationAPI, ILocation} from 'entities/location';

import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import Select from 'shared/ui/Select/Select';

import InputName from 'shared/ui/Input/InputName';

import {debounce} from 'lodash';

import {toast} from 'react-toastify';

import {t} from '@lingui/core/macro';

import ModalBlockButton from '../../../../shared/ui/Button/ModalBlockButton/ModalBlockButton';

import styles from '../Cities/Cities.module.scss';

interface ILocationsFormProps {
    onSuccess: () => void;
    item?: ILocation;
    onClose: () => void;
}

interface ILocationsFormState {
    zone: number;
    country?: number;
    city?: number;
    location: string;
    ip: string;
}

const LOCATION_NAME_REGEX = /^[a-zA-Zа-яА-Я0-9\- ().,"'«»“”]+$/;
const MIN_LOCATION_NAME_LENGTH = 2;
const DEBOUNCE_DELAY = 500;

const LocationsForm: React.FC<ILocationsFormProps> = ({item, onSuccess, onClose}) => {
    const defaultValues = useMemo(() => ({
        zone: item?.city.country.zone.id || undefined,
        country: item?.city.country.id || undefined,
        city: item?.city.id || undefined,
        location: item?.name || '',
        ip: item?.ip || '',
    }), [item]);
    const {register, control, handleSubmit, watch, formState, setValue} = useForm<ILocationsFormState>({
        defaultValues,
        mode: 'onChange',
    });
    const {isValid, isDirty, errors} = formState;

    const [searchQuery, setSearchQuery] = useState('');
    const [duplicateError, setDuplicateError] = useState('');
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [lastCityId, setLastCityId] = useState<number | null>(null);

    const [zoneId, countryId, cityId, locationName] = watch(['zone', 'country', 'city', 'location']);
    const isNew = !item;

    const {data: zones = []} = zoneAPI.useGetZonesQuery();
    const {data: countries = []} = countryAPI.useGetCountriesQuery(zoneId, {skip: !zoneId});
    const {data: cities = []} = cityAPI.useGetCityQuery(countryId ?? 0, {skip: !countryId});
    const {data: foundLocations = []} = locationAPI.useSearchLocationQuery(
        { cityId: cityId || 0, query: searchQuery || (item?.name ?? '') },
        { skip: !cityId || (!searchQuery && !item?.name) },
    );
    const [createLocation] = locationAPI.useCreateLocationMutation();
    const [updateLocation] = locationAPI.useUpdateLocationMutation();

    const zoneOptions = useMemo(() =>
        zones.map(zone => ({ value: zone.id, label: zone.name })),
    [zones],
    );

    const countryOptions = useMemo(() =>
        countries.map(country => ({ value: country.id, label: country.name })),
    [countries],
    );

    const cityOptions = useMemo(() =>
        cities.map(city => ({ value: city.id, label: city.name })),
    [cities],
    );

    const filteredLocations = useMemo(() =>
        foundLocations.filter(location => location.id !== item?.id),
    [foundLocations, item?.id],
    );

    useEffect(() => {
        if (cityId && cityId !== lastCityId) {
            if (locationName) {
                setSearchQuery(locationName);
            } else if (item?.name) {
                setSearchQuery(item.name);
            }
            setIsUserTyping(true);
        }
        setLastCityId(cityId ?? 0);
    }, [cityId, locationName, item?.name, lastCityId]);

    useEffect(() => {
        const debouncedSearch = debounce((value: string) => {
            if (value.length >= MIN_LOCATION_NAME_LENGTH && (isUserTyping || cityId !== lastCityId)) {
                setSearchQuery(value);
            }
        }, DEBOUNCE_DELAY);

        debouncedSearch(locationName);
        return () => debouncedSearch.cancel();
    }, [locationName, isUserTyping, cityId, lastCityId]);

    useEffect(() => {
        if (!searchQuery) return;
        const exactMatch = filteredLocations.find(
            location => location.name.toLowerCase() === locationName?.toLowerCase(),
        );
        setDuplicateError(exactMatch ? 'Такая локация уже существует в выбранном городе' : '');
    }, [filteredLocations, locationName, searchQuery]);

    useEffect(() => {
        if (item && zoneId === item.city.country.zone.id) return;
        if (zoneId) {
            setValue('country', undefined);
            setValue('city', undefined);
            setValue('location', '');
            setSearchQuery('');
        }
    }, [zoneId, setValue, item]);

    const getLocationsErrorMessage = useCallback(() => {
        if (duplicateError) return duplicateError;
        if (errors.location?.type === 'required') return 'Название локации обязательно';
        if (errors.location?.type === 'minLength') return `Минимальная длина - ${MIN_LOCATION_NAME_LENGTH} символа`;
        if (errors.location?.type === 'pattern') return 'Допустимы буквы, цифры, тире, скобки, кавычки и точки';
        return '';
    }, [duplicateError, errors.location?.type]);
    
    const handleLocationChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setIsUserTyping(true);
        setValue('location', e.target.value, { shouldDirty: true, shouldValidate: true });
    }, [setValue]);

    const isSubmitDisabled = !isValid || (!isNew && !isDirty)
        || !!duplicateError || !!errors.location || cities.length === 0;

    const onSubmit = useCallback(async(formState: ILocationsFormState) => {
        const selectedCity = cities.find(c => c.id === formState.city);
        if (!selectedCity) return;
        const locationData = {
            name: formState.location,
            city: `/api/v1/cities/${selectedCity.id}`,
            ip: formState.ip,
        };
        if (isNew) {
            await createLocation(locationData).unwrap();
            toast.info(t`Локация успешно добавлена`);
        } else {
            await updateLocation({ id: item.id, ...locationData }).unwrap();
            toast.info(t`Локация успешно обновлена`);

        }
        onSuccess();
    }, [cities, createLocation, isNew, item?.id, onSuccess, updateLocation]);

    const showLocationsSuggestions = isUserTyping && foundLocations.length > 0 &&
        locationName?.length >= MIN_LOCATION_NAME_LENGTH &&
        (!item || locationName.toLowerCase() !== item.name.toLowerCase());

    return (
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <ModalBodyWrapper>
                <FormGroupWithError
                    className={styles.formBlock}
                    label="Зона"
                    error={errors.zone?.message}
                >
                    <Select
                        control={control}
                        name="zone"
                        data={zoneOptions}
                        rules={{required: 'Выберите зону'}}
                        defaultValue={defaultValues.zone}
                        cleanable
                    />
                </FormGroupWithError>
                {zoneId  && (
                    <FormGroupWithError
                        className={styles.formBlock}
                        label="Страна"
                        error={errors.country?.message}
                    >
                        <Select
                            control={control}
                            name="country"
                            data={countryOptions}
                            rules={{
                                required: 'Выберите страну',
                            }}
                            defaultValue={defaultValues.country}
                            cleanable
                        />
                    </FormGroupWithError>
                )}
                {countryId  &&  (
                    <>
                        {cities.length === 0 ? (
                            <div className={styles.warning}>
                                Для выбранной страны нет городов. Пожалуйста, добавьте город на странице{' '}
                                <a href={AppUrls.ADMIN_CITIES}>Города</a>
                            </div>
                        ) : (
                            <FormGroupWithError
                                className={styles.formBlock}
                                label="Город"
                                error={errors.city?.message}
                            >
                                <Select
                                    control={control}
                                    name="city"
                                    data={cityOptions}
                                    rules={{required: 'Выберите город'}}
                                    defaultValue={defaultValues.city}
                                    cleanable
                                />
                            </FormGroupWithError>
                        )}
                    </>
                )}
                {cityId  && cities.length > 0 && (
                    <FormGroupWithError
                        className={styles.formBlock}
                        label="Локация"
                        error={getLocationsErrorMessage()}
                    >
                        <InputName
                            control={control}
                            name="location"
                            placeholder="Введите название локации"
                            register={register}
                            defaultValue={locationName}
                            onChange={handleLocationChange}
                            rules={{
                                required: true,
                                pattern: LOCATION_NAME_REGEX,
                                minLength: MIN_LOCATION_NAME_LENGTH,
                            }}
                        />
                    </FormGroupWithError>
                )}
                {showLocationsSuggestions && (
                    <div className={styles.suggestions}>
                        <div className={styles.suggestionsTitle}>Найдены локации:</div>
                        {filteredLocations.map(location => (
                            <div key={location.id} className={styles.foundCity}>
                                {location.name}
                            </div>
                        ))}
                    </div>
                )}
                {cityId  && cities.length > 0 && (
                    <FormGroupWithError
                        className={styles.formBlock}
                        label="IP"
                    >
                        <InputName
                            control={control}
                            name="ip"
                            placeholder="Введите IP локации"
                            register={register}
                            defaultValue={defaultValues.ip}
                        />
                    </FormGroupWithError>
                )}
            </ModalBodyWrapper>
            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Изменить`}
                primaryButtonType="submit"
                primaryButtonDisabled={isSubmitDisabled}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default React.memo(LocationsForm);
